import { useAiRecommendationCta } from '@cobliteam/ai';
import AIIcon from '@cobliteam/ai/src/components/AIIcon';
import { Analytics } from '@cobliteam/analytics';
import type { AiRecommendation } from '@cobliteam/hooks';
import { useAiRecommendationsStore } from '@cobliteam/hooks';
import { permissionsConfig } from '@cobliteam/hooks/src/useAiRecommendationsStore/useAiRecommendationsStore';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useTranslation } from '@cobliteam/i18n';
import usePermissions from '@cobliteam/permissions/src/hooks/usePermissions.tsx';
import Button from '@cobliteam/ui/components/Button';
import Carousel, { CarouselContent, CarouselItem, useCarousel } from '@cobliteam/ui/components/Carousel';
import Tag from '@cobliteam/ui/components/Tag';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import CriticalityTag from '@cobliteam/ui-business/components/CriticalityTag';

const criticalityOrder: Record<string, number> = { critical: 0, urgent: 1, important: 2, suggestive: 3 };
const MAX_VISIBLE = 3;

const RecommendationCard = ({ recommendation, isImprovement, allCompleted }: { recommendation: AiRecommendation; isImprovement: boolean; allCompleted: boolean }) => {
  const { t } = useTranslation('app-header');
  const { ctaButton } = useAiRecommendationCta({
    component: 'ai-recommendation-floating-card',
    journeyId: recommendation.journeyId,
    aiPrompt: recommendation.aiPrompt,
  });

  const hideFooter = !isImprovement && allCompleted;

  return (
    <div className='flex w-full flex-col gap-3'>
      {/* Criticality Tag */}
      {!hideFooter && recommendation.criticality && <CriticalityTag criticality={recommendation.criticality} />}

      {/* Title */}
      <Typography variant='description-title'>{recommendation.journeyName}</Typography>

      {/* Description */}
      <Typography variant='description-list-default' color='secondary' className='text-start'>
        {recommendation.description}
      </Typography>

      {/* Bottom: time + CTA */}
      {!hideFooter && (
        <div className='flex items-center justify-between gap-2'>
          {recommendation.estimatedMinutes > 0 ? (
            <Tag
              variant='outlined'
              color='default'
              label={t('aiRecommendations.durationMinutes', { count: recommendation.estimatedMinutes, minutes: recommendation.estimatedMinutes })}
              icon={<Icon name='clock' size='xs' />}
            />
          ) : (
            <span />
          )}
          {ctaButton}
        </div>
      )}
    </div>
  );
};

const CarouselNav = () => {
  const { t } = useTranslation('app-header');
  const { scrollPrev, scrollNext, canScrollPrev, canScrollNext } = useCarousel();

  const handleScrollNext = () => {
    Analytics.track('Ai Recommendation scroll next clicked', {
      component: 'ai-recommendation-floating-card',
    });

    scrollNext();
  };

  const handleScrollPrev = () => {
    Analytics.track('Ai Recommendation scroll prev clicked', {
      component: 'ai-recommendation-floating-card',
    });

    scrollPrev();
  };

  return (
    <div className='mt-4 flex items-center justify-between'>
      <Button variant='link' color='primary' size='sm' startIcon={<Icon name='arrow-left' size='xs' />} disabled={!canScrollPrev} onClick={handleScrollPrev}>
        {t('aiRecommendations.previousRecommendation')}
      </Button>
      <Button variant='link' color='primary' size='sm' endIcon={<Icon name='arrow-right' size='xs' />} disabled={!canScrollNext} onClick={handleScrollNext}>
        {t('aiRecommendations.nextRecommendation')}
      </Button>
    </div>
  );
};

const AiRecommendationFloatingCard = () => {
  const { aiRecommendations } = useFeatureFlags();
  const { isAccessAllowed } = usePermissions();

  const { recommendations, improvementRecommendations, progress, isError, floatingCardDismissed, dismissFloatingCard } = useAiRecommendationsStore();

  const allowedRecommendations = recommendations.filter((item) => {
    return isAccessAllowed(permissionsConfig[item.journeyId]);
  });

  const allowedImprovementRecommendations = improvementRecommendations.filter((item) => {
    return isAccessAllowed(permissionsConfig[item.journeyId]);
  });

  const sorted = [
    ...allowedRecommendations.map((r) => ({ recommendation: r, isImprovement: false })),
    ...allowedImprovementRecommendations.map((r) => ({ recommendation: r, isImprovement: true })),
  ]
    .sort((a, b) => (criticalityOrder[a.recommendation.criticality] ?? 99) - (criticalityOrder[b.recommendation.criticality] ?? 99))
    .slice(0, MAX_VISIBLE);

  if (!(aiRecommendations && sorted.length > 0) || floatingCardDismissed || isError) return null;

  const allCompleted = progress.totalJourneys > 0 && progress.completedJourneys >= progress.totalJourneys;

  return (
    <div className='fixed bottom-4 left-[calc(var(--sidebar-width-icon)+1rem)] z-[1003] w-[358px]'>
      <div
        className='relative flex w-full overflow-hidden rounded-2xl shadow-lg'
        style={{
          border: '1px solid transparent',
          backgroundImage: 'linear-gradient(#F8F7FD, #F8F7FD), linear-gradient(90deg, #0745cf 0%, #9847EE 100%)',
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
        }}
      >
        {/* Gradient top border */}
        <div className='absolute top-0 right-0 left-0 h-1' style={{ background: 'linear-gradient(to right, #0745cf, #9847EE)' }} />

        <div className='flex w-full flex-col gap-3 px-4 pt-5 pb-4'>
          {/* Icon + Dismiss row */}
          <div className='flex items-center justify-between'>
            <div
              className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl'
              style={{
                border: '2.5px solid transparent',
                backgroundImage: 'linear-gradient(#F8F7FD, #F8F7FD), linear-gradient(90deg, #0745cf 0%, #9847EE 100%)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
              }}
            >
              <AIIcon size={20} />
            </div>
            <Button variant='ghost' color='default' size='condensed' icon={<Icon name='x' size='sm' />} onClick={() => dismissFloatingCard()} aria-label='Fechar' />
          </div>

          {/* Carousel of recommendations */}
          <Carousel opts={{ align: 'start' }} className='[&>div]:!rounded-none flex-col'>
            <CarouselContent className='ml-0'>
              {sorted.map(({ recommendation: rec, isImprovement }) => (
                <CarouselItem key={rec.journeyId} className='pl-0'>
                  <RecommendationCard recommendation={rec} isImprovement={isImprovement} allCompleted={allCompleted} />
                </CarouselItem>
              ))}
            </CarouselContent>
            {sorted.length > 1 && <CarouselNav />}
          </Carousel>
        </div>
      </div>
    </div>
  );
};

export default AiRecommendationFloatingCard;
